import { zodResolver } from '@hookform/resolvers/zod';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { BiPencil, BiTrash } from 'react-icons/bi';
import { PiDotsThree } from 'react-icons/pi';
import { z } from 'zod';

import { ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import AddButton from '@/components/Button/AddButton';
import { Input } from '@/components/Inputs';
import { LoaderSpinner } from '@/components/LoaderSpinner/LoaderSpinner';
import { Popup, PopupSection, type PopupSectionItem } from '@/components/Popup/Popup';
import { Tile } from '@/components/Tile/Tile';
import { UnitInput } from '@/components/UnitInput/UnitInput';
import { useClickOutside } from '@/hooks/useClickOutside';
import { describePosition } from '@/utils/position';
import type { VolumeUnit } from '@/utils/unitConversion';

import { currentLocalTime, timeMaskProps } from '../../logbookTime';
import { useEntryFix } from '../../useEntryFix';
import {
  entryCostUsd,
  isCostTyped,
  switchFigureUnit,
  type FigureOrigin,
  type VolumeFigure,
} from '../mappers';
import { TIME_RE, volumeWord, type OilEntry } from '../types';

import s from './OilEntriesSection.module.scss';

interface OilEntriesSectionProps {
  entries: OilEntry[];
  /** The unit a new entry starts in: the user's volume preference. */
  defaultVolumeUnit: VolumeUnit;
  editingId: string | null;
  addingNew: boolean;
  onStartAdd(): void;
  onStartEdit(id: string): void;
  onCancel(): void;
  onSaveEdit(id: string, patch: Partial<OilEntry>): void;
  onAddNew(values: Omit<OilEntry, 'id'>): void;
  onRemove(id: string): void;
}

export function OilEntriesSection({
  entries,
  defaultVolumeUnit,
  editingId,
  addingNew,
  onStartAdd,
  onStartEdit,
  onCancel,
  onSaveEdit,
  onAddNew,
  onRemove,
}: OilEntriesSectionProps) {
  return (
    <Tile>
      <section className={s.section}>
        <h6 className={s.sectionTitle}>Bunker & Oil Entries</h6>

        {entries.length === 0 && !addingNew && (
          <p className={s.empty}>Please add an entry to get started.</p>
        )}

        {entries.map((entry, index) =>
          entry.id === editingId ? (
            <OilEntryEdit
              key={entry.id}
              number={index + 1}
              entry={entry}
              defaultVolumeUnit={defaultVolumeUnit}
              onCancel={onCancel}
              onSubmit={(values) => onSaveEdit(entry.id, values)}
            />
          ) : (
            <OilEntryItem
              key={entry.id}
              number={index + 1}
              entry={entry}
              onEdit={() => onStartEdit(entry.id)}
              onDelete={() => onRemove(entry.id)}
            />
          ),
        )}

        {addingNew ? (
          <OilEntryEdit
            number={entries.length + 1}
            defaultVolumeUnit={defaultVolumeUnit}
            onCancel={onCancel}
            onSubmit={(values) => onAddNew(values)}
          />
        ) : (
          <AddButton label="Bunker Entry" onClick={onStartAdd} />
        )}
      </section>
    </Tile>
  );
}

interface OilEntryItemProps {
  number: number;
  entry: OilEntry;
  onEdit(): void;
  onDelete(): void;
}

function OilEntryItem({ number, entry, onEdit, onDelete }: OilEntryItemProps) {
  const [opened, setOpened] = useState(false);
  const ref = useClickOutside<HTMLButtonElement>(() => setOpened(false));

  const actionsList: PopupSectionItem[] = [
    { label: 'Edit', onClick: onEdit, icon: <BiPencil /> },
    { label: 'Delete', onClick: onDelete, icon: <BiTrash /> },
  ];

  return (
    <article className={s.inputsGroup}>
      <div className={s.groupTitle}>
        <p>Entry #{number}</p>
        <button type="button" className={s.actions} onClick={() => setOpened((o) => !o)} ref={ref}>
          <PiDotsThree size={20} />
          {opened && (
            <Popup>
              <PopupSection items={actionsList} title="" onItemClick={() => setOpened(false)} />
            </Popup>
          )}
        </button>
      </div>
      <section className={s.inputs}>
        <Field label="Time" value={entry.time} />
        <Field label="Location" value={entry.location} />
        <Field
          label={`Bunker Taken ${volumeWord(entry.bunkerTakenUnit)}`}
          value={entry.bunkerTakenUsg}
        />
        <Field
          label={`Cost Per ${volumeWord(entry.costPerUnit)} after Tax USD`}
          value={entry.costPerUsgAfterTaxUsd}
        />
        <Field label="Cost USD" value={entry.costUsd} />
        <Field label="Cost Local Currency" value={entry.costLocalCurrency} />
        <Field
          label={`Total ${volumeWord(entry.totalOnboardUnit)} Aboard`}
          value={entry.totalOnboardUsg}
        />
      </section>
    </article>
  );
}

interface OilEntryEditProps {
  number: number;
  entry?: OilEntry;
  defaultVolumeUnit: VolumeUnit;
  onCancel(): void;
  onSubmit(values: Omit<OilEntry, 'id'>): void;
}

const FIELD_MAX = 50;
const LOCATION_MAX = 120;

const fieldString = z.string().max(FIELD_MAX, `Max ${FIELD_MAX} characters`);

const numericField = z
  .string()
  .refine(
    (v) => v === '' || (!Number.isNaN(Number(v)) && Number(v) >= 0),
    'Please enter an amount of 0 or more.',
  );

const entrySchema = z.object({
  time: z.string().refine((v) => !v || TIME_RE.test(v), 'Use HH:MM format (00:00–23:59)'),
  location: z.string().max(LOCATION_MAX, `Max ${LOCATION_MAX} characters`),
  bunkerTakenUsg: numericField,
  costPerUsgAfterTaxUsd: numericField,
  costUsd: numericField,
  costLocalCurrency: fieldString,
  totalOnboardUsg: numericField,
});

type EntryFormValues = z.infer<typeof entrySchema>;

function OilEntryEdit({ number, entry, defaultVolumeUnit, onCancel, onSubmit }: OilEntryEditProps) {
  // Each volume figure has a unit of its own; in a new entry it follows the preference until one
  // is picked beside the field.
  const [pickedUnits, setPickedUnits] = useState<Partial<Record<VolumeFigure, VolumeUnit>>>(() =>
    entry
      ? {
          bunkerTakenUsg: entry.bunkerTakenUnit,
          costPerUsgAfterTaxUsd: entry.costPerUnit,
          totalOnboardUsg: entry.totalOnboardUnit,
        }
      : {},
  );
  const unitOf = (figure: VolumeFigure): VolumeUnit => pickedUnits[figure] ?? defaultVolumeUnit;
  const units = {
    bunkerTakenUnit: unitOf('bunkerTakenUsg'),
    costPerUnit: unitOf('costPerUsgAfterTaxUsd'),
    totalOnboardUnit: unitOf('totalOnboardUsg'),
  };
  const {
    control,
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<EntryFormValues>({
    resolver: zodResolver(entrySchema),
    mode: 'onChange',
    defaultValues: {
      time: entry?.time ?? currentLocalTime(),
      location: entry?.location ?? '',
      bunkerTakenUsg: entry?.bunkerTakenUsg ?? '',
      costPerUsgAfterTaxUsd: entry?.costPerUsgAfterTaxUsd ?? '',
      costUsd: entry?.costUsd ?? '',
      costLocalCurrency: entry?.costLocalCurrency ?? '',
      totalOnboardUsg: entry?.totalOnboardUsg ?? '',
    },
  });
  // A new entry starts where the boat is — bunkering happens in port, so as a place name, the way
  // the daily log names it.
  const locating = useEntryFix(!entry, { getValues, setValue }, async (coords) => {
    const { formattedAddress } = await describePosition(coords.latitude, coords.longitude);
    // ponytail: a long address is cut at the limit rather than failing Add on text nobody typed.
    return { location: formattedAddress.slice(0, LOCATION_MAX) };
  });

  // Picking a unit re-keys that one figure: it converts here, and nothing else changes — not the
  // entry's other figures, not the other entries, not the user's preference — until Save. Origins
  // keep switching back from drifting.
  const origins = useRef<Partial<Record<VolumeFigure, FigureOrigin>>>({});
  const unitProps = (figure: VolumeFigure) => ({
    unit: unitOf(figure),
    noAutoConvert: true,
    onUnitChange: (next: string) => {
      const to = next as VolumeUnit;
      const origin = switchFigureUnit(
        figure,
        getValues(figure),
        origins.current[figure],
        unitOf(figure),
        to,
      );
      origins.current[figure] = origin;
      setValue(figure, origin.shown, { shouldValidate: true, shouldDirty: true });
      setPickedUnits((picked) => ({ ...picked, [figure]: to }));
    },
  });

  // Cost USD fills itself in from the bunker taken and its price until the user types a cost of
  // their own; clearing it hands it back. A unit switch leaves it be: the cost does not change.
  const [costTyped, setCostTyped] = useState(() => !!entry && isCostTyped(entry));
  const fillCost = () => {
    if (costTyped) return;
    setValue('costUsd', entryCostUsd({ ...getValues(), ...units }), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <form
      onSubmit={handleSubmit((values) => onSubmit({ ...values, ...units }))}
      className={s.inputsGroup}
    >
      <div className={s.groupTitle}>
        <p>Entry #{number}</p>
      </div>
      <section className={s.editInputs}>
        <Input
          label="Time"
          placeholder="HH:MM"
          error={errors.time?.message}
          {...timeMaskProps(register('time'))}
        />
        <Input
          label="Location"
          placeholder={locating ? 'Locating…' : 'IGY Marina Cabo San Lucas'}
          suffixNode={locating && <LoaderSpinner size={16} fullHeight={false} label="" />}
          error={errors.location?.message}
          {...register('location')}
        />
        <Controller
          control={control}
          name="bunkerTakenUsg"
          render={({ field }) => (
            <UnitInput
              kind="volume"
              label={`Bunker Taken ${volumeWord(units.bunkerTakenUnit)}`}
              type="number"
              step="any"
              min={0}
              placeholder="e.g. 1935.65"
              error={errors.bunkerTakenUsg?.message}
              value={field.value ?? ''}
              onChange={(value) => {
                field.onChange(value);
                fillCost();
              }}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
              {...unitProps('bunkerTakenUsg')}
            />
          )}
        />
        <Controller
          control={control}
          name="costPerUsgAfterTaxUsd"
          render={({ field }) => (
            <UnitInput
              kind="perVolume"
              label={`Cost Per ${volumeWord(units.costPerUnit)} after Tax USD`}
              type="number"
              step="any"
              min={0}
              placeholder="e.g. 6.44"
              error={errors.costPerUsgAfterTaxUsd?.message}
              value={field.value ?? ''}
              onChange={(value) => {
                field.onChange(value);
                fillCost();
              }}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
              {...unitProps('costPerUsgAfterTaxUsd')}
            />
          )}
        />
        <Input
          label="Cost USD"
          type="number"
          step="any"
          min={0}
          placeholder="e.g. 12467.39"
          error={errors.costUsd?.message}
          {...register('costUsd', { onChange: (e) => setCostTyped(e.target.value !== '') })}
        />
        <Input
          label="Cost Local Currency"
          placeholder="250059.67 Peso"
          error={errors.costLocalCurrency?.message}
          {...register('costLocalCurrency')}
        />
        <Controller
          control={control}
          name="totalOnboardUsg"
          render={({ field }) => (
            <UnitInput
              kind="volume"
              label={`Total Onboard`}
              placeholder="e.g. 2850"
              type="number"
              step="any"
              min={0}
              error={errors.totalOnboardUsg?.message}
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
              {...unitProps('totalOnboardUsg')}
            />
          )}
        />
      </section>
      <div className={s.groupItemActions}>
        <ButtonOutlinedSmall type="button" onClick={onCancel}>
          Cancel
        </ButtonOutlinedSmall>
        <ButtonSmall type="submit">{entry ? 'Save' : 'Add'}</ButtonSmall>
      </div>
    </form>
  );
}

interface FieldProps {
  label: string;
  value?: string;
}

function Field({ label, value }: FieldProps) {
  return (
    <article className={s.field}>
      <span className={`body-medium ${s.fieldLabel}`}>{label}</span>
      <div className={s.fieldValue}>{value && value.length ? value : '-'}</div>
    </article>
  );
}
